// Полоса «расчёт заблокирован» в карточке человека.
//
// Момент, ради которого она существует: человек уехал с вахты, за ним висит билет и форма, и
// решение «что с этим делать» сегодня принимается устно и забывается. Заблокированный расчёт —
// последняя точка, где об этих деньгах ещё можно вспомнить.
//
// Полоса не просто сообщает о блокировке, а называет каждый долг с суммой и даёт принять по
// нему решение прямо здесь. Отправить человека «искать долги в другом разделе» значит получить
// незакрытое увольнение: там он увидит список, но не поймёт, чего от него хотят.
//
// Кнопки не сереют без права: они объясняют, чьё это решение. Серая кнопка не доводит нажатие
// до обработчика, и со стороны это неотличимо от сломанной программы.

import { useState } from 'react';

import { RiLockLine } from '@remixicon/react';

import type { DebtDecisionKind } from '@/shared/api/endpoints/losses';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import { TextArea } from '@/shared/ui/text-area';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';

import { useDecideDebt, useFinalSettlement } from '../queries';

/** Решение по долгу принимает руководство: оно закрывает увольнение и признаёт потерю. */
const DECIDE_PERMISSION = 'core.debt.decide';

const РЕШЕНИЯ: { kind: DebtDecisionKind; label: string; hint: string }[] = [
  {
    kind: 'deduct_from_final',
    label: 'Удержать из расчёта',
    hint: 'сумма уйдёт в удержания финального расчёта',
  },
  {
    kind: 'write_off',
    label: 'Списать',
    hint: 'деньги признаются потерянными и попадут в отчёт о невозвратных потерях',
  },
  {
    kind: 'keep_active',
    label: 'Оставить долг',
    hint: 'долг остаётся действующим: человек может вернуться на вахту',
  },
  {
    kind: 'external_collection',
    label: 'Передать на взыскание',
    hint: 'дальше долгом занимаются юристы',
  },
];

export function FinalSettlementNotice({ employeeId }: { employeeId: string }) {
  const состояние = useFinalSettlement(employeeId);
  const решить = useDecideDebt(employeeId);
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, DECIDE_PERMISSION);

  const [открыт, setОткрыт] = useState<string | null>(null);
  const [основание, setОснование] = useState('');
  const [замечание, setЗамечание] = useState<string | null>(null);
  const [подтверждаемое, setПодтверждаемое] = useState<string | null>(null);

  const данные = состояние.data;
  // Полоса — про УВОЛЕННОГО. Долги есть у половины работающих (форма, обучение), и полоса,
  // висящая у каждого второго, перестаёт читаться вместе с тем случаем, ради которого
  // заведена. У работающего человека долг это график удержаний, а не блокировка.
  if (данные === undefined || !данные.blocked || !данные.is_fired) {
    return null;
  }

  async function применить(debtId: string, kind: DebtDecisionKind, сумма: number) {
    if (!можно) {
      setЗамечание(
        'Решение по долгу принимает руководство. Попросите его закрыть этот долг — до этого расчёт останется заблокированным.',
      );
      return;
    }
    if (основание.trim().length < 10) {
      setЗамечание(
        'Напишите основание: через полгода по этим деньгам будут спрашивать, а «списать» не объясняет ничего.',
      );
      return;
    }
    // Списание — необратимое признание потери денег. Один клик без подтверждения тут
    // означает, что случайное попадание в кнопку списывает тысячи: подтверждаем суммой.
    if (kind === 'write_off' && подтверждаемое !== debtId) {
      setПодтверждаемое(debtId);
      setЗамечание(
        `Списание вернуть нельзя: ${formatRubles(сумма)} будут признаны потерянными и попадут в отчёт о невозвратных потерях. Нажмите «Списать» ещё раз, если это верно.`,
      );
      return;
    }

    setЗамечание(null);
    try {
      await решить.mutateAsync({ debtId, decision: kind, basis: основание.trim() });
    } catch (ошибка) {
      // Отказ сервера обязан дойти словами: иначе человек видит, что «ничего не произошло»,
      // и решает, что долг закрыт, — а он остался.
      const конверт = (ошибка as { envelope?: { message?: string; next_action?: string } })
        ?.envelope;
      const причина = конверт?.message ?? (ошибка instanceof Error ? ошибка.message : '');
      setЗамечание(
        [`Решение не сохранено: ${причина || 'сервер отказал'}`, конверт?.next_action]
          .filter(Boolean)
          .join('. '),
      );
      return;
    }
    setПодтверждаемое(null);
    setОткрыт(null);
    setОснование('');
  }

  return (
    <section className='flex flex-col gap-3 rounded-xl border border-warning-base bg-bg-white-0 px-4 py-3'>
      <div className='flex items-start gap-3'>
        <RiLockLine className='mt-0.5 size-5 shrink-0 text-warning-base' />
        <div className='flex flex-col gap-1'>
          <span className='text-label-sm text-text-strong-950'>Финальный расчёт заблокирован</span>
          <span className='text-paragraph-sm text-text-sub-600'>
            {данные.reason ?? 'по каждому долгу нужно решение'}
          </span>
          <span className='text-paragraph-xs text-text-sub-600'>
            {countLabel(данные.pending.length, ['долг ждёт', 'долга ждут', 'долгов ждут'])} решения
            {данные.decided > 0 ? `; решено уже ${данные.decided}` : ''}
          </span>
        </div>
      </div>

      <ul className='flex flex-col gap-2'>
        {данные.pending.map((долг) => (
          <li
            key={долг.debt_id ?? долг.loss_type}
            className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 px-3 py-2'
          >
            <div className='flex items-baseline justify-between gap-3'>
              <span className='text-label-sm text-text-strong-950'>{долг.title}</span>
              <span className='text-label-sm tabular-nums text-text-strong-950'>
                {formatKopecks(долг.amount)}
              </span>
            </div>

            {открыт === долг.debt_id ? (
              <div className='flex flex-col gap-2'>
                <TextArea
                  id={`основание-${долг.debt_id ?? ''}`}
                  label='Основание решения'
                  value={основание}
                  onChange={setОснование}
                  rows={2}
                  placeholder='Что именно произошло и на чём решение держится'
                />
                <div className='flex flex-wrap gap-2'>
                  {РЕШЕНИЯ.map((решение) => (
                    <Button.Root
                      key={решение.kind}
                      type='button'
                      variant='neutral'
                      mode='stroke'
                      size='xsmall'
                      onClick={() => void применить(долг.debt_id ?? '', решение.kind, долг.amount)}
                    >
                      {решение.label}
                    </Button.Root>
                  ))}
                </div>
                <span className='text-paragraph-xs text-text-sub-600'>
                  {РЕШЕНИЯ.map((решение) => `${решение.label} — ${решение.hint}`).join('; ')}
                </span>
              </div>
            ) : (
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => {
                  setОткрыт(долг.debt_id ?? null);
                  setЗамечание(null);
                }}
              >
                Принять решение
              </Button.Root>
            )}
          </li>
        ))}
      </ul>

      {замечание === null ? null : <Hint.Root>{замечание}</Hint.Root>}
    </section>
  );
}
